import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const apiService = {
  // About
getAbout: () => api.get('/about'),
updateAbout: (data) => api.put('/admin/about', data),
  
  // Experiences
  getExperiences: () => api.get('/experiences'),
  getExperience: (id) => api.get(`/experiences/${id}`),
  createExperience: (data) => api.post('/admin/experiences', data),
  updateExperience: (id, data) => api.put(`/admin/experiences/${id}`, data),
  deleteExperience: (id) => api.delete(`/admin/experiences/${id}`),
  
  // Projects
  getProjects: (params) => api.get('/projects', { params }),
  getProject: (id) => api.get(`/projects/${id}`),
  getProjectBySlug: (slug) => api.get(`/projects/slug/${slug}`),
  getFeaturedProjects: () => api.get('/projects/featured'),
  createProject: (data) => api.post('/admin/projects', data),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  updateProjectStatus: (id, status) => api.patch(`/admin/projects/${id}/status`, { status }),

  // Hire Requests
  getHireRequests: () => api.get('/admin/hire-requests'),
  getHireRequest: (id) => api.get(`/admin/hire-requests/${id}`),
  updateHireStatus: (id, status) => api.patch(`/admin/hire-requests/${id}/status`, { status }),
  
  // Blog
  getBlogPosts: (params) => api.get('/blog', { params }),
  getBlogPost: (id) => api.get(`/blog/${id}`),
  getBlogPostBySlug: (slug) => api.get(`/blog/slug/${slug}`),
  createBlogPost: (data) => api.post('/admin/blog', data),
  updateBlogPost: (id, data) => api.put(`/admin/blog/${id}`, data),
  deleteBlogPost: (id) => api.delete(`/admin/blog/${id}`),
  updateBlogStatus: (id, status) => api.patch(`/admin/blog/${id}/status`, { status }),
  
  // Skills
  getSkills: () => api.get('/skills'),
  getSkillsByCategory: (category) => api.get(`/skills/category/${category}`),
  getFeaturedSkills: () => api.get('/skills/featured'),
  createSkill: (data) => api.post('/admin/skills', data),
  updateSkill: (id, data) => api.put(`/admin/skills/${id}`, data),
  deleteSkill: (id) => api.delete(`/admin/skills/${id}`),
  
  // Services
  getServices: () => api.get('/services'),
  
  // Testimonials
  getTestimonials: () => api.get('/testimonials'),
  getFeaturedTestimonials: () => api.get('/testimonials/featured'),
  createTestimonial: (data) => api.post('/admin/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/admin/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/admin/testimonials/${id}`),
  approveTestimonial: (id) => api.patch(`/admin/testimonials/${id}/approve`),
  
  // Hire
  submitHireRequest: (data) => api.post('/hire', data),
  getHireRequests: () => api.get('/admin/hire-requests'),
  updateHireStatus: (id, status) => api.patch(`/admin/hire-requests/${id}/status`, { status }),
  
  // Mentorship
  submitMentorship: (data) => api.post('/mentorship', data),
  getMentorshipRequests: () => api.get('/admin/mentorship-requests'),
  updateMentorshipStatus: (id, status) => api.patch(`/admin/mentorship-requests/${id}/status`, { status }),
  
  // Contact
  submitContact: (data) => api.post('/contact', data),
  getContacts: () => api.get('/admin/contacts'),
  markContactRead: (id) => api.patch(`/admin/contacts/${id}/read`),
  replyToContact: (id, data) => api.post(`/admin/contacts/${id}/reply`, data),
  
  // Newsletter
  subscribeNewsletter: (email) => api.post('/newsletter/subscribe', { email }),
  unsubscribeNewsletter: (email) => api.post('/newsletter/unsubscribe', { email }),
  
  // GitHub
  getGitHubRepos: () => api.get('/github/repos'),
  getGitHubStats: () => api.get('/github/stats'),
  // Auth
  login: (credentials) => api.post('/api-login.php', credentials),
  logout: () => api.post('/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getDashboardActivity: () => api.get('/admin/dashboard/activity'),
  
  // Analytics
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  getAnalyticsCharts: (params) => api.get('/admin/analytics/charts', { params }),
  
  // Media
  uploadMedia: (formData) => api.post('/admin/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteMedia: (id) => api.delete(`/admin/media/${id}`),
  getMedia: () => api.get('/admin/media'),
  
  // SEO
  getSEO: () => api.get('/admin/seo'),
  updateSEO: (page, data) => api.put(`/admin/seo/${page}`, data),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Resume
  getResumeVersions: () => api.get('/admin/resume'),
  uploadResume: (formData) => api.post('/admin/resume/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  generateResume: (data) => api.post('/admin/resume/generate', data),
  deleteResume: (id) => api.delete(`/admin/resume/${id}`),
  
  // Notifications
  getNotifications: () => api.get('/admin/notifications'),
  markNotificationRead: (id) => api.patch(`/admin/notifications/${id}/read`),
  markAllNotificationsRead: () => api.patch('/admin/notifications/read-all'),
};

export default api;